import React, { FC, ReactNode, useEffect, useState } from 'react';
import {
  DataGrid,
  DataGridHeader,
  DataGridProps,
  GridColumnHeaderParams,
  GridRenderCellParams,
  GridRowParams,
} from '@thingslog/ui-components';
import { useTranslation } from 'react-i18next';
import { CompanyDto } from '@thingslog/repositories';
import { Checkbox } from '@mui/material';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';

const DeviceSearchTable: FC<DeviceSearchTableProps> = ({
  companies,
  selectedCompanyId,
  onCompanySelect,
  ...props
}: DeviceSearchTableProps) => {
  const { t } = useTranslation();
  const [rows, setRows] = useState<DeviceSearchRow[]>([]);

  useEffect(() => {
    setRows(
      companies.map((company: CompanyDto) => ({
        id: company.id,
        companyName: company.name,
      }))
    );
  }, [companies]);

  const handleRowClick = (params: GridRowParams<DeviceSearchRow>): void => {
    const company = companies.find((entry: CompanyDto) => entry.id === Number(params.id));

    if (company) {
      onCompanySelect(company);
    }
  };

  return (
    <DataGrid
      rows={rows}
      pageSize={10}
      autoHeight
      disableSelectionOnClick
      disableColumnMenu
      hideFooterSelectedRowCount
      onRowClick={handleRowClick}
      columns={[
        {
          field: 'selected',
          headerName: '',
          width: 72,
          sortable: false,
          filterable: false,
          disableColumnMenu: true,
          align: 'center',
          renderHeader: (): ReactNode => null,
          renderCell: (params: GridRenderCellParams<unknown, DeviceSearchRow>): ReactNode => (
            <Checkbox
              checked={selectedCompanyId === Number(params.id)}
              onClick={(event: React.MouseEvent<HTMLButtonElement>): void => {
                event.stopPropagation();

                const company = companies.find(
                  (entry: CompanyDto) => entry.id === Number(params.id)
                );

                if (company) {
                  onCompanySelect(company);
                }
              }}
              icon={<RadioButtonUncheckedIcon fontSize="small" />}
              checkedIcon={<RadioButtonCheckedIcon fontSize="small" />}
              size="small"
            />
          ),
        },
        {
          field: 'companyName',
          headerName: t('companies_table_name') || 'Company name',
          flex: 1,
          minWidth: 240,
          sortable: false,
          renderHeader: (params: GridColumnHeaderParams): ReactNode => (
            <DataGridHeader headerName={params.colDef.headerName!} />
          ),
          renderCell: (params: GridRenderCellParams<string, DeviceSearchRow>): ReactNode => (
            <div className="py-2 text-sm font-medium text-slate-900">{params.value}</div>
          ),
        },
      ]}
      {...props}
    />
  );
};

interface DeviceSearchTableProps extends Omit<DataGridProps, 'columns' | 'rows'> {
  companies: CompanyDto[];
  selectedCompanyId: number | null;
  onCompanySelect: (company: CompanyDto) => void;
}

interface DeviceSearchRow {
  id: number;
  companyName: string;
}

export default DeviceSearchTable;
